import React, { useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withStyles, Tooltip, IconButton, Typography } from '@material-ui/core';
import { FindInPage } from '@material-ui/icons';
import { Redirect } from 'react-router-dom';
import { hasPermission, createHistory, hasAtLeastOnePermission } from '../../sc-utilities';
import CustomerList from './CustomerList';
import { informationColors } from '../../../themes';

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    }
});

const passwordComplexity = (passwordComplexity, translations) => {
    switch (passwordComplexity) {
        case 'securaze_basic':
            return <Typography variant={'caption'}>{`Securaze ${translations['basic'] || 'basic'}`}</Typography>;
        case 'securaze_standard':
            return <Typography variant={'caption'}>{`Securaze ${translations['standard'] || 'standard'}`}</Typography>;
        case 'microsoft':
            return <Typography variant={'caption'}>{`${translations['microsoft'] || 'Microsoft'}`}</Typography>;
        case 'nist':
            return <Typography variant={'caption'}>{translations['national_institute'] || 'National Institute of Standards and Technology'}</Typography>;
        default:
            return <Typography variant={'caption'}>n/a</Typography>;
    }
};

const Customer = props => {
    const tableRef = useRef();
    const { classes, columns, translations, permissions } = props;

    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, customerID: null, redirectPath: '' });

    const redirectHandler = (path, customerID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, customerID: customerID, redirectPath: path })
    };
    let visible = columns && columns['Customer'];

    const tableColumns = useMemo(() => {
        return [{
            title: translations["action"] || "Action", field: 'Action',
            hidden: visible ? visible['Action'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            filtering: false,
            render: rowData => <>
                {
                    hasPermission(permissions, 'customer_load') ? <Tooltip title={translations["customer_details"] || "Customer details"}>
                        <IconButton
                            id={`edit_customer_${rowData.CustomerID}`}
                            component='div'
                            onClick={() => redirectHandler(`/admin/customers/details?customerID=${rowData.CustomerID}`)}
                        >
                            <FindInPage />
                        </IconButton>
                    </Tooltip>
                        : null
                }
            </>
        },
        {
            title: translations["name"] || 'Name', field: 'Name',
            hidden: visible ? visible['Name'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Name ? rowData.Name : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["namespace"] || 'Namespace', field: 'Namespace',
            hidden: visible ? visible['Namespace'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Namespace ? rowData.Namespace : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["status"] || 'Status', field: 'Status',
            hidden: visible ? visible['Status'] : false,
            filtering: true,
            lookup: { '1': translations['active'] || 'Active', '0': translations['inactive'] || 'Inactive' },
            defaultFilter: ['0', '1'],
            render: rowData => {
                return <>
                    <CustomChip
                        className={rowData.Status === 1 ? 'active' : 'inactive'}
                        key={'customer_status'}
                        value={rowData.Status === 1 ? translations['active'] || 'Active' : translations['inactive'] || 'Inactive'}
                    />
                </>
            }
        },
        {
            title: translations["password_complexity"] || 'Password complexity', field: 'PasswordComplexity',
            hidden: visible ? visible['PasswordComplexity'] : false,
            filtering: true,
            cellStyle: { minWidth: 200, maxWidth: 200 },
            lookup: { 'securaze_basic': `Securaze ${translations['basic'] || 'basic'}`, 'securaze_standard': `Securaze ${translations['standard'] || 'standard'}`, 'microsoft': `${translations['microsoft'] || 'Microsoft'}`, 'nist': translations['national_institute'] || 'National Institute of Standards and Technology' },
            defaultFilter: ['securaze_basic', 'securaze_standard', 'microsoft', 'nist'],
            render: rowData => {
                return <Typography variant={'caption'}>{passwordComplexity(rowData.PasswordComplexity, translations)}</Typography>;
            }
        }
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes, translations, visible])

    if (!hasAtLeastOnePermission(permissions, ['customer_load'])) {
        tableColumns.shift();
    };
    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }
    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'customer_list') ?
                <CustomerList
                    tableColumns={tableColumns}
                    redirectHandler={redirectHandler}
                    tableRef={tableRef}
                />
                :
                null}
        </div>
    );
};

const CustomChip = props => {
    const { keyValue, value, className } = props;
    let possibleStyles = {
        inactive: {
            color: informationColors.error.dark,
        },
        active: {
            color: informationColors.success.main
        }
    }
    return (
        <Typography
            variant={'caption'}
            key={keyValue}
            className={className}
            style={possibleStyles[className]}>{value === 'n/a' ? value : capitalize(value)}</Typography>
    );
};

const capitalize = (s) => {
    if (typeof s !== 'string') return ''
    return s.charAt(0).toUpperCase() + s.slice(1)
}

const mapStateToProps = state => {
    return {
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || []
    };
};

export default connect(
    mapStateToProps,
    null
)(withStyles(styles)(Customer));